// Lightweight global "jobs changed" signal — the web equivalent of FFAppState().jobAdded.
//
// The Schedule page loads the jobs list ONCE per session and then reuses that cached
// list on every subsequent visit (fast even with thousands of jobs). It only re-queries
// when a job is mutated somewhere OUTSIDE the Schedule page — add / edit / reschedule /
// delete / complete — which flips `dirty = true` via the axios interceptor. Changes made
// inside the Schedule page reload it in place (and clear the flag), so they don't cause a
// second fetch. Manual reload always forces a fresh fetch regardless of this flag.
let loaded = false;
let dirty = false;
let pending = [];              // optimistic jobs awaiting the real backend record
const listeners = new Set();   // notified when the pending overlay changes

export const jobsBus = {
  // Last jobs/weather payload, kept so returning to Schedule is instant (survives the
  // page component unmounting on navigation).
  cache: { jobs: null, weather: null, jobWx: null },
  markChanged() { dirty = true; },
  shouldFetch() { return !loaded || dirty; },
  markLoaded() { loaded = true; dirty = false; },

  // ---- Optimistic insert overlay (instant "new job appears" on Schedule) ----
  addOptimistic(job) { pending = [...pending, { ...job, _ts: Date.now() }]; this._notify(); },
  // Mark an optimistic job as backed by a real record — it stays visible until the
  // real job lands in a fetched list (then merge() prunes it, avoiding duplicates).
  confirmOptimistic(tempId, realId) { pending = pending.map((p) => (p.id === tempId ? { ...p, _realId: realId } : p)); this._notify(); },
  removeOptimistic(tempId) { pending = pending.filter((p) => p.id !== tempId); this._notify(); },
  // Merge fetched jobs with still-pending optimistic jobs. Prunes any optimistic job
  // whose real record is already present, plus any stale (>60s) leftover — so there are
  // never duplicate or orphaned cards.
  merge(fetched) {
    const list = fetched || [];
    const ids = new Set(list.map((j) => j.id));
    const now = Date.now();
    pending = pending.filter((p) => !(p._realId && ids.has(p._realId)) && now - (p._ts || 0) < 60000);
    const extra = pending.filter((p) => !ids.has(p.id));
    return extra.length ? [...list, ...extra] : list;
  },
  subscribe(fn) { listeners.add(fn); return () => listeners.delete(fn); },
  _notify() { listeners.forEach((fn) => { try { fn(); } catch { /* noop */ } }); },
};

// True when a successful axios response was a job MUTATION (POST/PUT/PATCH/DELETE to a
// `/jobs` path). `/weather/jobs-batch` and plain GETs are intentionally excluded.
export function isJobMutation(config, status) {
  const method = (config?.method || "").toLowerCase();
  if (!["post", "put", "patch", "delete"].includes(method)) return false;
  if (status != null && (status < 200 || status >= 300)) return false;
  return /\/jobs(\/|\?|$)/.test(config?.url || "");
}
